import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { all_routes } from "../../../core/router/all_routes";
import api from "../../../core/api/config";
import GovHeader from "../../components/GovHeader";
import BotaoAjuda from "../../components/BotaoAjuda";
import TermoModal from "../components/TermoModal";
import { setPerfilAtivo } from "../../../core/redux/access/accessActions";
import { useDispatch } from "react-redux";

const Signin = () => {
  const route = all_routes;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const [successMessage, setSuccessMessage] = useState(null);
  const errorMessage = location.state?.error;

  const [mostrarTermoModal, setMostrarTermoModal] = useState(false);
  const [acessosPendentes, setAcessosPendentes] = useState([]);
  const dispatch = useDispatch();

  useEffect(() => {
    const mensagem = localStorage.getItem("mensagemCadastro");
    if (mensagem) {
      setSuccessMessage(mensagem);
      localStorage.removeItem("mensagemCadastro");
    }
  }, []);

  useEffect(() => {
    const storedEmail = localStorage.getItem("rememberedEmail");
    const storedPassword = localStorage.getItem("rememberedPassword");

    if (storedEmail && storedPassword) {
      setEmail(storedEmail);
      setPassword(storedPassword);
      setRememberMe(true);
    }
  }, []);

  const togglePasswordVisibility = () => {
    setPasswordVisible((prevState) => !prevState);
  };

  const validateFields = () => {
    let errors = {};
    if (!email.trim()) errors.email = "Este campo é obrigatório.";
    if (!password.trim()) errors.password = "Este campo é obrigatório.";
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const continueAfterTermAccept = async (acessos) => {
    localStorage.setItem("mostrarAviso", "true");

    //  salva acessos para o Dashboard decidir se abre o modal
    localStorage.setItem("acessosUsuario", JSON.stringify(acessos));

    if (acessos.length === 1) {
      try {
        const acesso = acessos[0];

        await api.post("/api/oauth/definir-acesso", {
          acesso_id: acesso.id,
        });

        // salva como ativo
        localStorage.setItem("acessoAtivo", JSON.stringify(acesso));
      } catch (err) {
        console.error("Erro ao definir acesso automático:", err);
        setError("Erro ao definir o acesso. Tente novamente.");
        return;
      }
    }

    //  SEMPRE VAI PARA DASHBOARD
    navigate(route.dashboard);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!validateFields()) {
      setLoading(false);
      return;
    }

    try {
      // 1 LOGIN OAUTH
      const response = await api.post("/api/oauth/token", {
        grant_type: "password",
        // eslint-disable-next-line no-undef
        client_id: process.env.REACT_APP_CLIENT_ID,
        // eslint-disable-next-line no-undef
        client_secret: process.env.REACT_APP_CLIENT_SECRET,
        username: email,
        password: password,
        scope: "",
      });

      const token = response.data.access_token;
      const acessos = response.data.acessos || [];

      localStorage.setItem("token", token);

      localStorage.removeItem("acessoAtivo");
      localStorage.removeItem("perfilAtivo");

      // lembrar usuário
      if (rememberMe) {
        localStorage.setItem("rememberedEmail", email);
        localStorage.setItem("rememberedPassword", password);
      } else {
        localStorage.removeItem("rememberedEmail");
        localStorage.removeItem("rememberedPassword");
      }

      // 2 BUSCAR DADOS DO USUÁRIO
      const userResponse = await api.get("/api/user/show");
      const userData = userResponse.data?.user;

      //  SALVAR PERFIL NO REDUX E LOCALSTORAGE
      dispatch(setPerfilAtivo(userData));
      localStorage.setItem("perfilAtivo", JSON.stringify(userData));

      if (!userData) {
        throw new Error("Usuário não encontrado.");
      }

      localStorage.setItem("userLogadoId", userData.id);

      //  USUÁRIO INATIVO
      if (!userData.ativo) {
        localStorage.removeItem("token");
        setError(
          "Seu usuário está bloqueado. Procure o administrador do sistema.",
        );
        setLoading(false);
        return;
      }

      // NÃO ACEITOU O TERMO
      if (!userData.aceite_do_termo) {
        setAcessosPendentes(acessos); // salva para continuar depois
        setMostrarTermoModal(true);
        setLoading(false);
        return;
      }

      // JÁ ACEITOU O TERMO
      await continueAfterTermAccept(acessos);
    } catch (err) {
      console.error("Erro ao tentar login:", err);

      if (err.response) {
        const { data } = err.response;

        switch (data.error) {
          case "invalid_grant":
            setError("As credenciais do usuário estão incorretas.");
            break;
          case "invalid_client":
            setError("Erro de autenticação. Contate o suporte.");
            break;
          case "invalid_request":
            setError("Verifique os campos e tente novamente.");
            break;
          default:
            setError(data.error_description || "Erro ao realizar login.");
        }
      } else if (err.code === "ECONNABORTED") {
        setError("Erro de conexão. Verifique sua internet.");
      } else {
        setError("Erro desconhecido. Tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptTerm = async () => {
    try {
      setLoading(true);

      const userId = localStorage.getItem("userLogadoId");

      await api.post(`/api/user/${userId}/edit?ativo=1`, {
        aceite_do_termo: true,
        data_do_termo: new Date().toISOString(),
      });

      setMostrarTermoModal(false);

      await continueAfterTermAccept(acessosPendentes);
    } catch (err) {
      console.error(err);
      setError("Erro ao registrar aceite do termo.");
    } finally {
      setLoading(false);
    }
  };

  const handleRecusarTermo = () => {
    localStorage.removeItem("token");
    setMostrarTermoModal(false);
    navigate(route.signin);
  };

  return (
    <>
      <div className="main-wrapper">
        <div className="account-content">
          <GovHeader />
          <div className="login-wrapper login-new">
            <div className="container">
              <div className="login-content user-login">
                <div className="login-logo">
                  <img src="/assets/img/logo.png" alt="Logotipo ConeCta-SP" />
                  <Link
                    to={route.dashboard}
                    className="login-logo mb-0 logo-white"
                  >
                    <img
                      src="/assets/img/logo-white.png"
                      alt="Logotipo branco do ConeCta-SP"
                    />
                  </Link>
                </div>

                <form onSubmit={handleLogin}>
                  <div className="login-userset">
                    <div className="login-userheading">
                      <h3>Bem-vindo(a) ao SisConeCta-SP</h3>
                    </div>

                    {successMessage && (
                      <div
                        className="alert alert-success alert-dismissible fade show position-relative"
                        role="alert"
                      >
                        {successMessage}
                        <button
                          type="button"
                          className="btn-close"
                          data-bs-dismiss="alert"
                          aria-label="Close"
                          onClick={() => setSuccessMessage(null)}
                        >
                          <i className="fas fa-xmark" />
                        </button>
                      </div>
                    )}

                    {errorMessage && (
                      <div className="alert alert-danger">{errorMessage}</div>
                    )}

                    {error && <div className="alert alert-danger">{error}</div>}

                    <div className="form-login">
                      <label className="form-label">Digite seu e-mail:</label>
                      <div className="form-addons">
                        <input
                          type="email"
                          className="form-control"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="seu@email.com"
                        />
                        <img
                          src="/assets/img/icons/mail.svg"
                          alt="Icone de e-mail"
                        />
                      </div>
                      {fieldErrors.email && (
                        <p className="text-danger">{fieldErrors.email}</p>
                      )}
                    </div>

                    <div className="form-login">
                      <label>Digite sua senha:</label>
                      <div className="pass-group">
                        <input
                          type={isPasswordVisible ? "text" : "password"}
                          className="pass-input form-control"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Sua senha"
                        />
                        <span
                          className={`fas toggle-password ${
                            isPasswordVisible ? "fa-eye" : "fa-eye-slash"
                          }`}
                          onClick={togglePasswordVisibility}
                        ></span>
                      </div>
                      {fieldErrors.password && (
                        <p className="text-danger">{fieldErrors.password}</p>
                      )}
                    </div>

                    <div className="form-login authentication-check">
                      <div className="row">
                        <div className="col-6">
                          <label className="checkboxs ps-4 mb-0 pb-0 line-height-1">
                            <input
                              type="checkbox"
                              checked={rememberMe}
                              onChange={(e) => setRememberMe(e.target.checked)}
                            />
                            <span className="checkmarks" />
                            Lembrar-me
                          </label>
                        </div>
                        <div className="col-6 text-end">
                          <Link
                            className="forgot-link"
                            to={route.forgotPassword}
                          >
                            Esqueceu sua senha?
                          </Link>
                        </div>
                      </div>
                    </div>

                    <div className="form-login">
                      <button
                        type="submit"
                        className="btn btn-login"
                        disabled={loading}
                      >
                        {loading ? "Carregando..." : "Entrar"}
                      </button>
                    </div>
                  </div>
                </form>
                <div className="signinform mt-3">
                  <h4>
                    Ainda não tem cadastro?{" "}
                    <Link to={route.register} className="hover-a">
                      Cadastre-se agora
                    </Link>
                  </h4>
                </div>
                <div className="login-logo ">
                  <img
                    src="assets/img/logo-fosp.png"
                    alt="Logotipo Fosp"
                    width="100px"
                  />
                </div>
              </div>
            </div>
          </div>
          <BotaoAjuda />
          <TermoModal
            show={mostrarTermoModal}
            onClose={handleRecusarTermo}
            onAccept={handleAcceptTerm}
            loading={loading}
            userNeedsToAcceptTerm={true}
            title="Termo de Confidencialidade"
          />
        </div>
      </div>
    </>
  );
};

export default Signin;
